import React, { useCallback, useEffect, useState } from "react";
import { Button, Space, Table, Tag } from "antd";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import paths from "routes/paths";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import usePaymentCoupons from "hooks/finance/usePaymentCoupons";
import CouponDetailsDrawer from "./CouponDetailsDrawer";
import CouponUsageChargeDetailsDrawer from "./CouponUsageChargeDetailsDrawer";

const formatCurrency = (amount, currency = "USD") => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "-";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch {
    return `${numericAmount.toFixed(2)} ${currency || "USD"}`;
  }
};

const formatDate = (value) => (value ? dayjs(value).format("YYYY-MM-DD hh:mm A") : "-");

export default function CouponUsagesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchCouponUsages, getCouponByCode, usagesLoading } = usePaymentCoupons();
  const [usages, setUsages] = useState([]);
  const [selectedUsage, setSelectedUsage] = useState(null);
  const [chargeDrawerOpen, setChargeDrawerOpen] = useState(false);
  const [couponDrawerOpen, setCouponDrawerOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [meta, setMeta] = useState({
    page: 0,
    number: 0,
    size: 500,
    totalElements: 0,
    totalPages: 0,
  });

  const page = Number(searchParams.get("page") || 0);
  const size = Number(searchParams.get("size") || 500);

  const loadUsages = useCallback(async () => {
    const result = await fetchCouponUsages({ page, size });
    setUsages(result?.content || []);
    setMeta(result?.meta || {});
  }, [fetchCouponUsages, page, size]);

  useEffect(() => {
    loadUsages();
  }, [loadUsages]);

  const openChargeDetails = (usage) => {
    setSelectedUsage(usage);
    setChargeDrawerOpen(true);
  };

  const openCouponDetails = async (usage) => {
    const fallbackCoupon = {
      id: usage?.couponId,
      code: usage?.couponCode,
      discountType: usage?.discountType,
      discountValue: usage?.discountValue,
      duration: usage?.duration,
      currency: usage?.currency,
      chargeCode: usage?.chargeCode,
      clientApp: usage?.clientApp,
    };
    setSelectedCoupon(fallbackCoupon);
    setCouponDrawerOpen(true);
    try {
      const coupon = await getCouponByCode(usage?.couponCode);
      setSelectedCoupon(coupon || fallbackCoupon);
    } catch {
      setSelectedCoupon(fallbackCoupon);
    }
  };

  const columns = [
    {
      title: "Coupon",
      key: "coupon",
      width: 150,
      render: (_, record) => (
        <div style={{ lineHeight: 1.25 }}>
          <div style={{ fontWeight: 700 }}>{record.couponCode || "-"}</div>
          <div style={{ color: "#6b7280", fontSize: 12 }}>{record.discountType || "-"}</div>
        </div>
      ),
    },
    {
      title: "Discount",
      key: "discount",
      width: 120,
      render: (_, record) => formatCurrency(record.discountAmount, record.currency),
    },
    {
      title: "Charge Code",
      dataIndex: "chargeCode",
      key: "chargeCode",
      width: 190,
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "Product",
      dataIndex: "clientApp",
      key: "clientApp",
      width: 150,
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "Payment",
      key: "payment",
      width: 130,
      render: (_, record) => (
        <Tag color={record.paymentStatus === "SUCCESS" ? "green" : "default"}>
          {record.paymentStatus || "-"}
        </Tag>
      ),
    },
    {
      title: "Used At",
      key: "usedAt",
      width: 170,
      render: (_, record) => formatDate(record.chargeCreatedAt),
    },
    {
      title: "Links",
      key: "links",
      width: 150,
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => openChargeDetails(record)}>
            Charge
          </Button>
          <Button type="link" size="small" onClick={() => openCouponDetails(record)}>
            Coupon
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="receipt"
          title="Coupon Usages"
          subtitle="Payment transactions where coupons were applied"
          className="mb-2 mt-2 flex-1"
        />

        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(paths.paymentCoupons)}
            style={{ borderColor: "#1677ff", color: "#1677ff" }}
          >
            Coupons
          </Button>
          <Button
            icon={<ReloadOutlined />}
            loading={usagesLoading}
            onClick={loadUsages}
            style={{ borderColor: "#15803d", color: "#15803d" }}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <Table
        rowKey={(record, index) =>
          `${record.chargeId || "charge"}-${record.couponCode || "coupon"}-${index}`
        }
        columns={columns}
        dataSource={usages}
        loading={usagesLoading}
        size="small"
        className="custom-table"
        scroll={{ x: 1060 }}
        pagination={{
          current: (meta?.page ?? meta?.number ?? page) + 1,
          pageSize: meta?.size ?? size,
          total: meta?.totalElements ?? usages.length,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} coupon usages`,
        }}
        onChange={(pagination) => {
          setSearchParams({
            page: String(pagination.current - 1),
            size: String(pagination.pageSize),
          });
        }}
      />

      <CouponUsageChargeDetailsDrawer
        open={chargeDrawerOpen}
        usage={selectedUsage}
        onClose={() => setChargeDrawerOpen(false)}
      />

      <CouponDetailsDrawer
        open={couponDrawerOpen}
        coupon={selectedCoupon}
        onClose={() => setCouponDrawerOpen(false)}
      />
    </div>
  );
}
